import { ChangeDetectionStrategy, Component, computed, inject, signal, ViewEncapsulation } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { DomSanitizer } from '@angular/platform-browser';
import { ButtonComponent } from '@examdojo/core/button';
import { IconComponent } from '@examdojo/core/icon';
import katex from 'katex';
import { breakdownSf, randomSfNumber, SfBreakdown } from './rounding-sf.model';

@Component({
  selector: 'dojo-rounding-sf',
  standalone: true,
  imports: [FormsModule, ButtonComponent, IconComponent],
  templateUrl: './rounding-sf.component.html',
  styleUrl: './rounding-sf.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  host: { class: 'block' },
})
export class RoundingSfComponent {
  private readonly sanitizer = inject(DomSanitizer);

  readonly value = signal(0.004076);
  readonly sf = signal(2);
  readonly inputText = signal('0.004076');

  readonly breakdown = computed<SfBreakdown>(() => breakdownSf(this.value(), this.sf()));

  readonly gridTemplateColumns = computed(() => {
    return this.breakdown()
      .digits.map((d) => {
        if (d.role === 'point') {
          return '12px';
        }
        if (d.role === 'negative') {
          return '16px';
        }
        return '28px';
      })
      .join(' ');
  });

  /** Same columns as the digit grid, plus a flexible overflow column for annotation text. */
  readonly annotationGridColumns = computed(() => {
    return `${this.gridTemplateColumns()} 1fr`;
  });

  /** CSS grid-column value spanning all "keep" digits (1-indexed). */
  readonly keepGridColumn = computed(() => {
    const digits = this.breakdown().digits;
    const firstKeep = digits.findIndex((d) => d.role === 'keep');
    const lastKeep = digits.reduce((acc, d, i) => (d.role === 'keep' ? i : acc), -1);
    if (firstKeep < 0) {
      return '';
    }
    return `${firstKeep + 1} / ${lastKeep + 2}`;
  });

  /** 1-indexed CSS grid column for the check digit. Empty string if none. */
  readonly checkCol = computed(() => {
    const idx = this.breakdown().digits.findIndex((d) => d.role === 'check');
    return idx >= 0 ? `${idx + 1}` : '';
  });

  /** CSS grid-column spanning from check digit to end (for annotation text overflow). */
  readonly checkSpanColumn = computed(() => {
    const col = this.checkCol();
    return col ? `${col} / -1` : '';
  });

  /** KaTeX-rendered check logic, e.g. "6 ≥ 5 (round up)". */
  readonly checkLogicHtml = computed(() => {
    const b = this.breakdown();
    const cmp = b.roundedUp ? '\\geq' : '<';
    const label = b.roundedUp ? '\\text{(round up)}' : '\\text{(stays)}';
    const latex = `${b.checkDigitValue} ${cmp} 5 \\; ${label}`;
    return this.sanitizer.bypassSecurityTrustHtml(katex.renderToString(latex, { throwOnError: false, output: 'html' }));
  });

  onInputChange(text: string): void {
    this.inputText.set(text);
    const parsed = parseFloat(text);
    if (!isNaN(parsed) && isFinite(parsed) && parsed !== 0) {
      this.value.set(parsed);
    }
  }

  onSfChange(newSf: number): void {
    this.sf.set(Math.max(1, Math.min(6, newSf)));
  }

  randomize(): void {
    const n = randomSfNumber();
    this.value.set(n);
    this.inputText.set(n.toString());
    this.sf.set(Math.floor(Math.random() * 3) + 1);
  }

  ordinal(n: number): string {
    const suffixes = ['th', 'st', 'nd', 'rd'];
    const v = n % 100;
    return n + (suffixes[(v - 20) % 10] || suffixes[v] || suffixes[0]);
  }
}
